import {
  type ClassificationAnswers,
  deriveProfile,
  hasPersonalData,
  isLowering,
  unansweredQuestions,
} from './classification';
import { kitGate1 } from './kitGate1';
import { CONTRACT_VERSION, type Profile } from './profiles';
import { REGIMES, type RegulatoryRow, regulatoryRowProblem } from './regulatory';
import {
  ROLE_LABELS,
  type RoleException,
  type RolesFile,
  canHoldBoth,
  missingRoles,
  roleWarnings,
  sameLogin,
} from './roles';

export type Provenance = {
  field: string;
  tool: string;
  model: string;
  prompt_version: string;
  at?: string;
  confirmed_by?: string;
  /** Why the assistant suggested it, where the draft says, such as a contract answer. */
  reason?: string;
};

export type Outcome = {
  purpose?: string;
  advantage?: string;
  measurement?: string;
  baseline?: string;
};

export type ContractTerms = {
  client_autonomy?: 'no' | 'later' | 'now';
  customer_acceptance_authority?: boolean;
  ipr?: 'client' | 'vivicta' | 'shared';
  ai_code?: 'allowed' | 'disclose' | 'not_allowed';
};

export const SSD_RISKS = ['Low', 'Medium', 'High', 'Critical'] as const;
export type SsdRisk = (typeof SSD_RISKS)[number];

/** Who stops the agents and when it was last tried (the kit's `kill_switch`). */
export type KillSwitch = {
  /** GitHub logins. */
  operator?: string;
  backup?: string;
  /** ISO date of the last stop test. */
  tested?: string;
  /** Link to the stop-test record: an issue or a workflow run. */
  evidence?: string;
};

/** What the SDLC kit records at Gate 1 beyond the portal's own questions. */
export type DeliveryControls = {
  /** INI-0042: the initiative this application belongs to. */
  initiative_id?: string;
  ssd_risk?: SsdRisk;
  data_flows?: string;
  kill_switch?: KillSwitch;
};

/** The Gate 1 working draft, as kept in the portal database. */
export type Gate1Draft = {
  outcome: Outcome;
  classification: {
    answers: ClassificationAnswers;
    chosen_profile?: Profile;
    reason?: string;
  };
  roles: RolesFile['roles'];
  exceptions?: RoleException[];
  /** Reasons given for "should-not" role combinations, by person. */
  combination_reasons?: Record<string, string>;
  regulatory: RegulatoryRow[];
  contract: ContractTerms;
  /** Absent in drafts started before the kit's Gate 1 records. */
  delivery?: DeliveryControls;
  /** Fields holding an unconfirmed assistant draft, e.g. "outcome.purpose". */
  unconfirmed?: string[];
  provenance?: Provenance[];
};

export const GATE1_SECTIONS = [
  'outcome',
  'classification',
  'roles',
  'regulatory',
  'contract',
  'delivery',
] as const;
export type Gate1Section = (typeof GATE1_SECTIONS)[number];

export const GATE1_SECTION_TITLES: Readonly<Record<Gate1Section, string>> = {
  outcome: 'Outcome',
  classification: 'Classification and profile',
  roles: 'People and roles',
  regulatory: 'Regulatory screening',
  contract: 'Contract and autonomy',
  delivery: 'Risk and kill switch',
};

const INITIATIVE_ID = /^INI-\d{4,}$/;
const PLACEHOLDER_INITIATIVE = 'INI-0000';
const LOGIN = /^[A-Za-z0-9-]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** The kit's `kill_switch.max_test_age_days`. */
export const MAX_STOP_TEST_AGE_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);

function stopTestProblems(tested: string | undefined, today: string) {
  if (!tested || !ISO_DATE.test(tested)) return ['Give the date of the last stop test'];
  const age = daysBetween(tested, today);
  if (age < 0) return ['The stop test date is in the future'];
  if (age > MAX_STOP_TEST_AGE_DAYS) {
    return [`The stop test is ${age} days old; test it again (at most ${MAX_STOP_TEST_AGE_DAYS})`];
  }
  return [];
}

/** What the kit's Gate 1 check needs that only Gate 1 can answer. */
export function deliveryProblems(delivery: DeliveryControls = {}, today: string) {
  const killSwitch = delivery.kill_switch ?? {};
  const id = delivery.initiative_id?.trim() ?? '';
  return [
    ...(INITIATIVE_ID.test(id) && id !== PLACEHOLDER_INITIATIVE
      ? []
      : ['Give the initiative ID, such as INI-0042']),
    ...(delivery.ssd_risk ? [] : ['Choose the SSD risk']),
    ...(delivery.data_flows?.trim() ? [] : ['Describe the data flows']),
    ...(LOGIN.test(killSwitch.operator ?? '') ? [] : ['Name the kill switch operator']),
    ...(LOGIN.test(killSwitch.backup ?? '') ? [] : ['Name a backup operator']),
    ...(killSwitch.operator && sameLogin(killSwitch.operator, killSwitch.backup ?? '')
      ? ['The backup must be someone other than the operator']
      : []),
    ...stopTestProblems(killSwitch.tested, today),
    ...(/^https:\/\/\S+$/.test(killSwitch.evidence ?? '') ? [] : ['Link the stop test record']),
  ];
}

export const OUTCOME_FIELDS: ReadonlyArray<{ id: keyof Outcome; label: string; hint: string }> = [
  { id: 'purpose', label: 'Purpose', hint: 'What the application is for, in one or two sentences.' },
  { id: 'advantage', label: 'Advantage', hint: 'Who gains what, compared with today.' },
  { id: 'measurement', label: 'Measurement', hint: 'How the gain will be measured.' },
  { id: 'baseline', label: 'Baseline and target', hint: "Today's value and the target; if unknown, who finds it and by when." },
];

export const emptyGate1Draft = (purpose = ''): Gate1Draft => ({
  outcome: { purpose },
  classification: { answers: {} },
  roles: {},
  regulatory: [],
  contract: {},
});

export const chosenProfile = (draft: Gate1Draft): Profile =>
  draft.classification.chosen_profile ??
  deriveProfile(draft.classification.answers).profile;

const unconfirmedIn = (draft: Gate1Draft, section: Gate1Section) =>
  (draft.unconfirmed ?? []).filter(path => path.split('.')[0] === section);

/** What each section still needs; a section is complete when its list is empty. */
export function gate1Readiness(
  draft: Gate1Draft,
  today = new Date().toISOString().slice(0, 10),
): Record<Gate1Section, string[]> {
  const derived = deriveProfile(draft.classification.answers);
  const profile = chosenProfile(draft);
  const personalData = hasPersonalData(draft.classification.answers);
  const draftsLeft = (section: Gate1Section) =>
    unconfirmedIn(draft, section).length > 0
      ? ['Confirm the assistant’s draft']
      : [];

  const outcome = [
    ...OUTCOME_FIELDS.filter(f => !draft.outcome[f.id]?.trim()).map(
      f => `${f.label} is empty`,
    ),
    ...draftsLeft('outcome'),
  ];

  const classification = [
    ...unansweredQuestions(draft.classification.answers).map(
      q => `Answer “${q.question}”`,
    ),
    ...(isLowering(derived.profile, profile) && !draft.classification.reason?.trim()
      ? [`Give a reason for choosing ${profile} below the suggested ${derived.profile}`]
      : []),
  ];

  const warnings = roleWarnings(draft.roles, profile, draft.exceptions, today);
  const roles = [
    ...missingRoles(draft.roles, profile, personalData).map(
      role => `Name ${/^[AEIOU]/.test(ROLE_LABELS[role]) ? 'an' : 'a'} ${ROLE_LABELS[role]}`,
    ),
    ...warnings
      .filter(
        w =>
          w.severity === 'should-not' &&
          !draft.combination_reasons?.[w.person]?.trim(),
      )
      .map(w => `Say why ${canHoldBoth(w)}`),
  ];

  const rows = new Map(draft.regulatory.map(row => [row.regime, row]));
  const regulatory = [
    ...REGIMES.map(regime =>
      regulatoryRowProblem(rows.get(regime.id) ?? { regime: regime.id }),
    ).filter(Boolean),
    ...draftsLeft('regulatory'),
  ];

  const c = draft.contract;
  const contract = [
    ...(c.client_autonomy ? [] : ['Say whether client autonomy authorisation is sought']),
    ...(typeof c.customer_acceptance_authority === 'boolean' ? [] : ['Say whether there is a Customer Acceptance Authority']),
    ...(c.ipr ? [] : ['Choose who owns the code']),
    ...(c.ai_code ? [] : ["Record the client's position on AI-generated code"]),
  ];

  const delivery = deliveryProblems(draft.delivery, today);

  return { outcome, classification, roles, regulatory, contract, delivery };
}

export const isGate1Ready = (draft: Gate1Draft, today?: string) =>
  Object.values(gate1Readiness(draft, today)).every(list => list.length === 0);

export const GATE1_PATH = '.vivicta/asdp/gate1.json';

/** The longest regulatory note: a reason and a follow-up, not a document. */
export const MAX_REGULATORY_NOTE_LENGTH = 1000;

/**
 * `.vivicta/asdp/gate1.json` (journey spec §7.1), with the Gate 1 answers in
 * the SDLC kit's terms under `kit` for the template to record.
 */
export function buildGate1File(draft: Gate1Draft, initiativeName: string) {
  const derived = deriveProfile(draft.classification.answers);
  const chosen = chosenProfile(draft);
  return {
    schema_version: 1,
    contract_version: CONTRACT_VERSION,
    outcome: draft.outcome,
    classification: {
      answers: draft.classification.answers,
      derived_profile: derived.profile,
      chosen_profile: chosen,
      reason: isLowering(derived.profile, chosen)
        ? draft.classification.reason ?? null
        : null,
    },
    regulatory: draft.regulatory,
    contract: draft.contract,
    provenance: draft.provenance ?? [],
    kit: kitGate1(draft, initiativeName),
  };
}
